// Turns a contribution's recorded workflow into timeline steps. Only server records are used:
// ActivityHistory rows (actor, status change, note, time) and, for contributions created before
// history was kept, the timestamp fields stored on the contribution itself. Nothing is inferred
// about who acted when the record does not say so.

export const REVIEW_EVENTS = ["approved", "rejected", "revision_requested"];
const MAJOR = ["created", "submitted", "resubmitted", "revision_requested", "approved", "rejected"];
const OPEN_FOR_REVIEW = ["pending_review", "resubmitted"];

// System messages the server stores on non-review events; they repeat the event name and are not
// shown. Review notes and attachment file names are kept.
const BOILERPLATE = /^(activity|contribution) (created as draft|submitted for review|resubmitted after revision|updated)\.?$/i;

const lower = value => String(value || "").toLowerCase();
const timeOf = value => {
    const t = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(t) ? t : null;
};

// The status transition is authoritative: an event named "submitted" that leaves the record in
// draft is a creation, and an unnamed event that moves it to approved is an approval.
export function eventKind(event) {
    const type = lower(event?.eventType || event?.type);
    const from = lower(event?.fromStatus);
    const to = lower(event?.toStatus);
    if (to === "draft" && !from) return "created";
    if (to && from !== to) {
        if (to === "pending_review") return "submitted";
        if (to === "resubmitted") return "resubmitted";
        if (REVIEW_EVENTS.includes(to)) return to;
    }
    if (["file_uploaded", "file_added", "evidence_file_uploaded"].includes(type)) return "attachment_added";
    if (["file_removed", "file_deleted", "evidence_file_removed"].includes(type)) return "attachment_removed";
    if (["created", "submitted", "resubmitted", "updated", "attachment_added", "attachment_removed", ...REVIEW_EVENTS].includes(type)) return type;
    return "other";
}

function fromHistory(history, currentUserId) {
    return history
        .map((event, index) => {
            const kind = eventKind(event);
            const note = String(event.note || "").trim();
            return {
                id: event.id || `event-${index}`,
                kind,
                major: MAJOR.includes(kind),
                at: event.createdAt || null,
                actorId: event.actorId || null,
                actorName: event.actorName || event.actor?.name || null,
                isYou: Boolean(currentUserId && event.actorId && String(event.actorId) === String(currentUserId)),
                note: note && !BOILERPLATE.test(note) ? note : "",
                derived: false,
                order: index,
            };
        })
        .sort((a, b) => (timeOf(a.at) ?? 0) - (timeOf(b.at) ?? 0) || a.order - b.order);
}

// Legacy records without history: one step per timestamp the contribution stores.
function fromRecord(activity) {
    const steps = [
        ["submitted", activity.submittedAt],
        ["revision_requested", activity.revisionRequestedAt],
        ["resubmitted", activity.resubmittedAt],
        ["approved", activity.approvedAt],
        ["rejected", activity.rejectedAt],
    ].filter(([, at]) => timeOf(at) != null);
    return steps
        .map(([kind, at], order) => ({ id: `record-${kind}`, kind, major: true, at, actorId: null, actorName: null, isYou: false, note: "", derived: true, order }))
        .sort((a, b) => timeOf(a.at) - timeOf(b.at) || a.order - b.order);
}

export function buildWorkflow(activity = {}, { ownerName = null, reviewerName = null, reviewerId = null, currentUserId = null } = {}) {
    const status = lower(activity.status);
    const history = Array.isArray(activity.history) ? activity.history : [];
    const events = history.length ? fromHistory(history, currentUserId) : fromRecord(activity);
    const derived = !history.length && events.length > 0;

    const submissions = events.filter(e => e.kind === "submitted" || e.kind === "resubmitted");
    const firstSubmission = submissions[0] || null;
    const lastSubmission = submissions[submissions.length - 1] || null;
    const reviews = events.filter(e => REVIEW_EVENTS.includes(e.kind));
    const lastReview = reviews[reviews.length - 1] || null;

    // Latest reviewer feedback: the note on the most recent review decision. The stored manager
    // note is used only for legacy records without history, and only once a decision exists.
    const reviewWithNote = [...reviews].reverse().find(e => e.note);
    let feedback = null;
    if (reviewWithNote) feedback = { kind: reviewWithNote.kind, note: reviewWithNote.note, at: reviewWithNote.at, actorName: reviewWithNote.actorName, isYou: reviewWithNote.isYou };
    else if (!history.length && REVIEW_EVENTS.includes(status) && String(activity.managerNote || "").trim()) {
        const legacy = [...events].reverse().find(e => e.kind === status) || null;
        feedback = { kind: status, note: String(activity.managerNote).trim(), at: legacy?.at || null, actorName: null, isYou: false };
    }

    // The next step that has not happened yet, with the person it waits on when the record names them.
    let pending = null;
    const isYou = id => Boolean(currentUserId && id && String(id) === String(currentUserId));
    if (status === "draft") pending = { kind: "submit", waitingOn: ownerName, waitingOnYou: isYou(activity.userId) };
    else if (OPEN_FOR_REVIEW.includes(status)) pending = { kind: "review", waitingOn: reviewerName, waitingOnYou: isYou(reviewerId) };
    else if (status === "revision_requested") pending = { kind: "revise", waitingOn: ownerName, waitingOnYou: isYou(activity.userId) };

    return {
        status,
        events,
        derived,
        missingActors: !derived && events.some(e => e.major && !e.actorName),
        firstSubmission,
        lastSubmission,
        lastReview,
        decision: ["approved", "rejected"].includes(status) ? lastReview : null,
        feedback,
        pending,
        final: ["approved", "rejected"].includes(status),
    };
}

// Major steps are always listed; runs of consecutive minor updates (edits, file changes) are kept
// but grouped so the approval path stays readable.
export function groupTimeline(events = []) {
    const rows = [];
    events.forEach(event => {
        if (event.major) { rows.push({ type: "event", event }); return; }
        const last = rows[rows.length - 1];
        if (last?.type === "minor") last.events.push(event);
        else rows.push({ type: "minor", events: [event] });
    });
    return rows;
}
